import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useRecommendationsApi from '../../api/recommendations';
import { useAxios } from './useAxios';
import { useUser } from './UserContext';

const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
  const axios = useAxios();
  const { trackEvents } = useRecommendationsApi();
  const { user, loading, isAuthenticated } = useUser();

  const userId = user?.id;

  const [favoriteIds, setFavoriteIds] = useState(() => new Set());
  const idsRef = useRef(favoriteIds);

  // Synchronisation hors rendu (autorisé par la règle)
  useEffect(() => {
    idsRef.current = favoriteIds;
  }, [favoriteIds]);

  useEffect(() => {
    if (!userId) {
      setFavoriteIds(new Set());
      return;
    }
    const controller = new AbortController();
    axios
      .get('/favorites/ids', { signal: controller.signal })
      .then((r) => setFavoriteIds(new Set(r.data.ids)))
      .catch(() => {});
    return () => controller.abort();
  }, [userId, axios]);

  // Stable : ne dépend que de setFavoriteIds
  const apply = useCallback(
    (id, on) =>
      setFavoriteIds((prev) => {
        if (prev.has(id) === on) return prev;
        const next = new Set(prev);
        if (on) next.add(id);
        else next.delete(id);
        return next;
      }),
    []
  );

  const toggleFavorite = useCallback(
    async (id) => {
      if (!id || !userId) return;
      const wasFavorite = idsRef.current.has(id);

      apply(id, !wasFavorite); // optimiste

      try {
        const { data } = await axios.post('/favorites/toggle', { userId, vehiculeAnnonceId: id });
        apply(id, data.favorited); // la vérité vient du serveur
        if (data.favorited) trackEvents([{ type: 'favorite', vehiculeAnnonceId: id }]);
      } catch {
        apply(id, wasFavorite); // rollback
      }
    },
    [userId, axios, apply, trackEvents]
  );

  const value = useMemo(() => ({ favoriteIds, toggleFavorite }), [favoriteIds, toggleFavorite]);
  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export default function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavorites doit être utilisé dans <FavoritesProvider>');
  return ctx;
}
